/* eslint-disable @typescript-eslint/no-unused-vars */
import { Fragment, useEffect, useRef, useState } from "react";
import { IconDotsVertical } from "@tabler/icons-react";
import {
  useShelfRowDeleteMutation,
  usePrintLabelsMutation,
} from "../../../hooks/shelveHooks";
import MessageModal from "../../../components/MessageModal";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import WalkingSpace from "./WalkingSpaceElement";
import RackLevelElement from "./RackInstanceElement";

interface RowProps {
  rowId: string;
  className?: string;
  rowType: string;
  name: string;
  idx: number;
  racks: any;
  rackLevels: any;
  outletShelveId: string;
  clientId: string;
  storageType: string;
  fetchShelves: () => void;
  onEdit: () => void;
}

const Row: React.FC<RowProps> = ({
  className,
  name,
  rowType,
  idx,
  rackLevels,
  outletShelveId,
  clientId,
  storageType,
  fetchShelves,
  rowId,
  onEdit,
}) => {
  const [rowDropdownOpen, setRowDropdownOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const rowDropdownRef = useRef<HTMLDivElement>(null);

  const profile = useSelector((state: any) => state.auth.profile);
  const clientType = profile?.clientType;

  const { mutate: deleteRow, isPending: isDeletingRow } =
    useShelfRowDeleteMutation(rowId);

  const { mutate: printLabels } = usePrintLabelsMutation(outletShelveId);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        rowDropdownRef.current &&
        !rowDropdownRef.current.contains(event.target as Node)
      ) {
        setRowDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const renderRackLevels = () => {
    return (rackLevels || []).map((rackLevel: any, index: number) => (
      <Fragment key={`rack-level-${rackLevel.id}`}>
        <RackLevelElement
          rackLevelId={rackLevel.id}
          rackLevelName={rackLevel.name}  
          rackLevel={rackLevel}
          fetchShelves={fetchShelves}
        />
      </Fragment>
    ));
  };

  const handleDeleteRow = () => {
    console.log("DELETE CLICKED");

    deleteRow(
      {},
      {
        onSuccess: () => {
          console.log("DELETE SUCCESS");
          toast.success("Row deleted successfully");
          setDeleteConfirmOpen(false);
          fetchShelves();
        },
        onError: (err: any) => {
          console.log("DELETE ERROR", err);
          toast.error(err.message || "Failed to delete row");
        },
      },
    );
  };

  const handlePrintLabels = () => {
    printLabels(undefined, {
      onSuccess: (res: any) => {
        toast.success("Print labels fetched successfully");

        const pdfUrl = res?.pdfUrl;

        if (pdfUrl) {
          window.open(pdfUrl, "_blank");
        } else {
          toast.error("PDF URL not found");
        }
        setRowDropdownOpen(false);
      },
      onError: (err: any) => {
        console.error("PRINT LABELS ERROR", err);
        toast.error(err.message || "Failed to fetch print labels");
      },
    });
  };

  return (
    <>
      <div className={`space-y-2 bg-[#FCFDF8] ${className || ""}`}>
        <div className="flex justify-between items-center px-2 pt-2">
          <div className="flex items-center space-x-2">
            <h4 className="text-sm font-medium">{name}</h4>
          </div>

          <div ref={rowDropdownRef} className="relative">
            <div
              onClick={() => setRowDropdownOpen(!rowDropdownOpen)}
              className="p-1 rounded-full border hover:bg-gray-100 cursor-pointer"
            >
              <IconDotsVertical size={20} />
            </div>

            {rowDropdownOpen && (
              <div className="absolute top-8 right-0 w-48 bg-white border rounded-md shadow-md divide-y text-sm text-[#404040] z-20">
                {/* <p
                  onClick={() => handlePrintLabels()}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer">
                  Print Labels
                </p> */}

                <p
                  onClick={() => {
                    onEdit();
                    setRowDropdownOpen(false);
                  }}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                >
                  Edit Row
                </p>
                {/* <p
                  onClick={() => setDeleteConfirmOpen(true)}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-red-600"
                >
                  Delete Row
                </p> */}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2 px-2">{renderRackLevels()}</div>
      </div>

      <MessageModal
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Delete Row"
        subtitle="Are you sure you want to delete this row and all its contents? This action cannot be undone."
        img="/img/junk.png"
        showFooter={true}
        closeButtonText="Cancel"
        actionButtonText="Confirm Delete"
        actionButtonOnClick={handleDeleteRow}
        isPending={isDeletingRow}
        size="md"
      />
    </>
  );
};

export default Row;
